import { ChangeEvent, ClipboardEvent, useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useLocation, useNavigate } from "react-router-dom";
import { endpointInstance } from "../methods/Methods";
import PaymentForms from "../components/PaymentForms";
import { Button, Col, Input, Row, Typography } from "antd";
import { StyleSheet } from "../StyleSheet";
import HeaderComponent from "../components/HeaderComponent";
import vars from "../vars";
import Loading from "../components/Loading";
import { images } from "../routes/ImageRoutes";
import stylesCss from "./landing.module.css";
import InfoCardComponent from "../components/InfoCardComponent";

const { Text } = Typography;

const Landing = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [clientID, setClientID] = useState<string>("");
    const [resultOfCaptcha, setResultOfCaptcha] = useState<string>("");
    const [btnDisable, setBtnDisable] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const refOfCaptcha = useRef<ReCAPTCHA>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const call = endpointInstance;

    const resetCaptchaState = (options?: { clearToken?: boolean }) => {
        const shouldClearToken = options?.clearToken ?? true;
        setResultOfCaptcha("");
        if (shouldClearToken) {
            endpointInstance.setToken("");
        }
        localStorage.removeItem("captchaToken");
        setBtnDisable(true);
        refOfCaptcha.current?.reset();
    };

    useEffect(() => {
        resetCaptchaState();
        setHasError(false);
        setMessage("");
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlId =
            params.get("id")
        const encryptedId = urlId ? urlId.replace(/ /g, "+").trim() : "";

        if (!encryptedId) return;

        setLoading(true);
        setHasError(false);
        setMessage("");

        call
            .validateSuscriptor(encryptedId)
            .then((response) => {
                const data = response.data.data?.[0];
                const contrato = data?.contrato
                    ? data.contrato.replace(/[^0-9]/g, "")
                    : "";

                if (data?.estatus === "true" && contrato.length === 10) {
                    setClientID(contrato);
                    return;
                }

                setMessage(
                    data?.mensaje ||
                    "No fue posible validar el identificador enviado en la URL."
                );
                setHasError(true);
            })
            .catch(() => {
                setMessage(
                    "Hubo un error al validar el identificador enviado en la URL."
                );
                setHasError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [location.search, call]);

    useEffect(() => {
        if (clientID.length === 10 && resultOfCaptcha) {
            endpointInstance.setToken(resultOfCaptcha);
            setBtnDisable(false);
        } else {
            setBtnDisable(true);
        }
    }, [clientID, resultOfCaptcha]);

    useEffect(() => {
        setHasError(false);
        setMessage("");
    }, [clientID]);

    const redirectToPayment = () => {
        navigate("/payments");
    };

    const getPaypalClient = async () => {
        const body = {
            sucursal: clientID.substring(0, 3),
            suscriptor: clientID.substring(3, 10),
        };
        await call
            .paypalID(body)
            .then((response) => {
                if (response.data.data[0].mensaje) {
                    localStorage.setItem("clientId", response.data.data[0].mensaje);
                }
            })
            .catch(() => { });
    };

    const validate = async () => {
        const body = {
            sucursal: clientID.substring(0, 3),
            suscriptor: clientID.substring(3, 10),
        };

        setLoading(true);

        await call
            .getUserActive(body)
            .then(async (response) => {
                if (
                    response.data.data[0].estatus === "true" &&
                    response.data.data[0].mensaje === "Con servicio"
                ) {
                    localStorage.setItem("idCliente", clientID);
                    await getPaypalClient();
                    redirectToPayment();
                } else if (response.data.data[0].mensaje === "Sin servicio") {
                    setMessage(
                        "Contrato sin servicio. Favor de revisar los datos proporcionados."
                    );
                    setHasError(true);
                } else if (response.data.data[0].respuesta) {
                    setMessage(response.data.data[0].respuesta);
                    setHasError(true);
                } else {
                    setMessage(
                        "Hubo un error al procesar su solicitud, intente más tarde."
                    );
                    setHasError(true);
                }
                setLoading(false);
            })
            .catch(() => {
                setMessage(
                    "Hubo un error al procesar su solicitud, intente más tarde."
                );
                setHasError(true);
                setLoading(false);
            });
    };


    const onChange = (value: any) => {
        setResultOfCaptcha(value);
    };

    const handleCopy = (e: ClipboardEvent<HTMLInputElement>) => {
        // Prevent copying when someone tries to copy from the input
        e.preventDefault();
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        // Prevent pasting when someone tries to paste into the input
        e.preventDefault();
    };

    return (
        <>
            {
                loading ?
                    <Loading />
                    :
                    <>
                        <HeaderComponent />
                        <Row style={styles.titleRow}>
                            <Text style={styles.textTitle}>
                                Paga en Línea tus servicios de Mega
                            </Text>
                        </Row>
                        <Row style={styles.formContainer}>
                            <Col style={styles.divContainer}>
                                <Text style={styles.subtitle}>
                                    Ingresa tu contrato a 10 dígitos
                                </Text>
                                <Input
                                    required
                                    type="text"
                                    value={clientID}
                                    placeholder="Ingresa tu contrato a 10 dígitos"
                                    style={styles.input}
                                    maxLength={10}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                        setClientID(e.target.value.replace(/[^0-9]/g, ""))
                                    }
                                    onCopy={handleCopy}
                                    onCut={handleCopy}
                                    onPaste={handlePaste}
                                />
                                <ReCAPTCHA
                                    ref={refOfCaptcha}
                                    sitekey={vars.sitekey}
                                    onChange={(value) => onChange(value)}
                                    theme="light"
                                    style={{ marginTop: 30, marginBottom: 20 }}
                                />                                {
                                    hasError && message ?
                                        <InfoCardComponent
                                            color="#FFC439" colorText="#000"
                                            description={message}
                                        />
                                        :
                                        null
                                }
                                <Button
                                    size='large'
                                    style={btnDisable ? styles.buttonDisabled : styles.buttonPrimary}
                                    disabled={btnDisable}
                                    onClick={() => validate()}
                                >
                                    INGRESAR
                                </Button>

                                <img
                                    src={images.cards}
                                    alt="Tarjetas"
                                    className={stylesCss.cards}
                                />
                            </Col>
                        </Row>
                        <PaymentForms />
                    </>
            }
        </>
    );
};

const styles = StyleSheet.create({
    titleRow: {
        marginTop: 10,
        display: 'flex',
        justifyContent: 'center',
    },
    textTitle: {
        fontSize: 40,
        color: 'black',
        fontWeight: 700,
        textAlign: 'center',
        lineHeight: 1.2
    },
    subtitle: {
        fontSize: 22,
        color: 'black',
        textAlign: 'center'
    },
    input: {
        width: 380,
        height: 40,
        backgroundColor: "#f0efff",
        textAlign: 'center',
        borderColor: 'transparent',
        marginTop: "1.5rem"
    },
    divContainer: {
        flexDirection: "column",
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonPrimary: {
        backgroundColor: '#007aff',
        borderRadius: 30,
        color: 'white',
        border: "none",
        width: 320,
        marginTop: 25,
    },
    buttonDisabled: {
        backgroundColor: '#787B7F',
        borderRadius: 30,
        color: 'white',
        border: "none",
        width: 320,
        marginTop: 25,
    },
    formContainer: {
        marginTop: "2rem",
        justifyContent: "center"
    },
    lineDivider: {
        borderColor: 'black',
        minWidth: "100%",
        width: "100%"
    }
});

export default Landing;
